/**
 * useResendInvitation — TanStack mutation over
 * `POST /admin/staff/invitations/:invitationId/resend` (no body).
 *
 * Error envelope (tagged union):
 *   - 'notPending' — 422 (the invitation is no longer pending).
 *   - 'notFound'   — 404 (no such invitation).
 *   - 'forbidden'  — 403 (caller is not root).
 *   - 'error'      — anything else.
 *
 * On success, invalidates the canonical invitations key (a resend
 * re-arms an expired invitation, flipping its derived status back to
 * 'invited' and refreshing its expiry).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { STAFF_INVITATIONS_QUERY_KEY } from './use-staff-invitations.js';

export type ResendInvitationError =
  | { readonly kind: 'notPending' }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error' };

export interface UseResendInvitationResult {
  readonly mutateAsync: (invitationId: string) => Promise<null>;
  readonly isPending: boolean;
}

export function useResendInvitation(): UseResendInvitationResult {
  const queryClient = useQueryClient();

  // Note: `useMutation` does not accept `void` as its first generic
  // because lint forbids `void` outside return types. Use `null` and
  // resolve with it on success — call sites await without consuming
  // the value.
  const mutation = useMutation<null, ResendInvitationError, string>({
    mutationFn: async (invitationId) => {
      const { response } = await apiClient.POST(
        '/admin/staff/invitations/{invitationId}/resend',
        { params: { path: { invitationId } } },
      );
      if (response.status === 422) {
        throw { kind: 'notPending' } satisfies ResendInvitationError;
      }
      if (response.status === 404) {
        throw { kind: 'notFound' } satisfies ResendInvitationError;
      }
      if (response.status === 403) {
        throw { kind: 'forbidden' } satisfies ResendInvitationError;
      }
      if (!response.ok) {
        throw { kind: 'error' } satisfies ResendInvitationError;
      }
      return null;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: STAFF_INVITATIONS_QUERY_KEY,
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
